/* eslint-disable @typescript-eslint/strict-boolean-expressions */
import {
  ColDef,
  GridReadyEvent,
  IServerSideDatasource,
} from '@ag-grid-community/core';
import AgGridSolid, { AgGridSolidRef } from '@ag-grid-community/solid';
import { ExcelSheetIcon } from '@assets';
import { Box, IconButton, Stack } from '@atgfr8/atg-ui-tailwind';
import { Button } from '@components';
import { getDB } from '@database';
import { useParams } from '@solidjs/router';
import {
  exportOrderHistoryToExcel,
  fetchOrderHistoryFinancial,
  fetchOrderHistoryGridData,
  HistoryGridPayload,
  OrderHistoryModel,
} from '@store/customers/customerDetails/orderHistory';
import { RefreshIcon } from '@atgfr8/icons';
import { formatDataTestId } from '@utils/dataTestIdFormatter';
import { routeOrderDetails } from '@utils/routeOrderDetails';
import { sdbm } from '@utils/utils';
import { atgLogo } from '@views/ltl';
import { DateTime } from 'luxon';
import { createEffect, createSignal } from 'solid-js';

type OrderHistoryGridProps = {
  orderHistoryColumns: ColDef[];
};

const filterTypeMapping = {
  equals: 'EQUALS',
  notEqual: 'NOT_EQUALS',
  greaterThan: 'GREATER_THAN',
  greaterThanOrEqual: 'GREATER_THAN_OR_EQUALS',
  lessThan: 'LESS_THAN',
  lessThanOrEqual: 'LESS_THAN_OR_EQUALS',
  contains: 'CONTAINS',
  notContains: 'NOT_CONTAINS',
  startsWith: 'STARTS_WITH',
  endsWith: 'ENDS_WITH',
  blank: 'BLANK',
  notBlank: 'NOT_BLANK',
  inRange: 'IN_RANGE',
};

const OrderHistoryGrid = (props: OrderHistoryGridProps) => {
  const routeParams = useParams();
  const [gridRef, setGridRef] = createSignal<AgGridSolidRef | null>(null);
  const [currentPayload, setCurrentPayload] =
    createSignal<HistoryGridPayload | null>(null);
  const [exportLoading, setExportLoading] = createSignal(false);

  const filterFieldMapping: Record<string, string> = {
    loadNumber: 'orderId',
  };

  const transformFilters = (inputFilters: { [key: string]: unknown }) => {
    const outputFilters = Object.keys(inputFilters).map((key) => {
      const mappedKey = filterFieldMapping[key] ?? key;
      const filter = inputFilters[key] as {
        filterType: string;
        type: keyof typeof filterTypeMapping;
        filter: string;
        dateFrom?: string | null;
        dateTo?: string | null;
      };
      if ((filter as { filterType: string }).filterType === 'date') {
        const dateFilters = [];

        if (Boolean(filter.dateFrom)) {
          dateFilters.push(
            DateTime.fromFormat(
              filter.dateFrom as string,
              'yyyy-MM-dd HH:mm:ss',
            ).toFormat('MM/dd/yyyy'),
          );
        }

        if (Boolean(filter.dateTo)) {
          dateFilters.push(
            DateTime.fromFormat(
              filter.dateTo as string,
              'yyyy-MM-dd HH:mm:ss',
            ).toFormat('MM/dd/yyyy'),
          );
        }
        return {
          field: mappedKey,
          filterType:
            filterTypeMapping[
              (filter as { type: keyof typeof filterTypeMapping }).type
            ],
          values: dateFilters,
        };
      }
      return {
        field: mappedKey,
        filterType:
          filterTypeMapping[
            (filter as { type: keyof typeof filterTypeMapping }).type
          ],
        values: [(filter as { filter: string }).filter],
      };
    });
    // Always include the customerId filter
    outputFilters.push({
      field: 'customerId',
      filterType: 'EQUALS',
      values: [routeParams.id as string],
    });

    return outputFilters;
  };

  const onGridReady = (params: GridReadyEvent) => {
    setGridRef(params);
    const dataSource = {
      getRows: async (params: {
        api: {
          paginationGetCurrentPage: () => number;
        };
        request: {
          filterModel: {
            [key: string]: {
              filter?: string;
              dateFrom?: string | null;
              dateTo?: null;
              filterType: 'date' | 'text' | 'set';
              type: string;
              values: string[];
            };
          };
          sortModel: {
            colId: string;
            sort: string;
          }[];
        };
        success: (data: {
          rowData: OrderHistoryModel[];
          rowCount: number;
        }) => void;
        fail: () => void;
      }) => {
        let orderBy = '';
        let sortBy = '';
        if (params.request.sortModel.length > 0) {
          orderBy = params.request.sortModel[0].colId;
          sortBy = params.request.sortModel[0].sort;
        }
        const filteredObject = {
          filters: transformFilters(params.request.filterModel),
          sort: {
            field: orderBy || 'pickUp',
            direction: sortBy || 'desc',
          },
          pageNumber: params.api.paginationGetCurrentPage() + 1,
          pageSize: 50,
        };
        setCurrentPayload(filteredObject as HistoryGridPayload);

        try {
          gridRef()?.api.showLoadingOverlay();

          const searchHash = sdbm(JSON.stringify(filteredObject));
          const customerId = parseInt(routeParams.id as string);
          const entry = await getDB()
            ?.orderHistory.where({
              hash: searchHash,
              customerId,
            })
            .first();

          // If the data is older than 10 minutes, re-fetch it.
          if (!entry || Date.now() - entry.date.getTime() > 60000 * 10) {
            const data = (await fetchOrderHistoryGridData(
              filteredObject as HistoryGridPayload,
            )) as unknown as {
              data: OrderHistoryModel[];
              totalCount: number;
            };

            const newEntry = {
              hash: searchHash,
              items: data.data,
              date: new Date(),
              totalCount: data.totalCount,
              customerId,
            };

            if (!!entry) {
              await getDB()?.orderHistory.update(searchHash, newEntry);
            } else {
              await getDB()?.orderHistory.add(newEntry);
            }

            params.success({
              rowData: data.data,
              rowCount: data.totalCount,
            });
          } else {
            params.success({
              rowData: entry.items,
              rowCount: entry.totalCount,
            });
          }

          const financialData = await fetchOrderHistoryFinancial(
            filteredObject as HistoryGridPayload,
          );
          if (financialData) {
            gridRef()?.api.setGridOption('pinnedBottomRowData', [
              {
                agent: 'Total:',
                revenue: financialData.totalRevenue,
                cost: financialData.totalCost,
                profit: financialData.totalProfit,
              },
            ]);
          }
        } catch (error) {
          params.fail();
        } finally {
          gridRef()?.api.hideOverlay();
        }
      },
    };
    params.api.setGridOption(
      'serverSideDatasource',
      dataSource as unknown as IServerSideDatasource,
    );
  };

  let previousId = routeParams.id;
  createEffect(() => {
    if (previousId !== routeParams.id) {
      previousId = routeParams.id;
      gridRef()?.api.refreshServerSide();
    }
  });

  return (
    <Stack class="h-full">
      <Box class="w-full flex justify-end p-2 border-gray-400 rounded-xs border-b-0 border bg-[#F8F8F8]">
        <IconButton
          onClick={async () => {
            const histories = await getDB()
              ?.orderHistory.filter(
                (o) => o.customerId === parseInt(routeParams.id as string),
              )
              .toArray();

            if (!!histories) {
              await getDB()?.orderHistory.bulkDelete(
                histories.map((o) => o.hash),
              );
            }

            gridRef()?.api.refreshServerSide({ purge: true });
            gridRef()?.api.purgeInfiniteCache();
          }}
          dataTestId="order-history-grid-refresh-icon-button"
        >
          <RefreshIcon />
        </IconButton>
        <Button
          label={
            <div class="flex items-center gap-1">
              <img src={ExcelSheetIcon} height="18px" alt="excelsheet" />
              Export to Excel
            </div>
          }
          variant="contained"
          size="sm"
          isLoading={exportLoading()}
          onClick={async () => {
            const payload = currentPayload();
            if (!payload) return;
            setExportLoading(true);
            await exportOrderHistoryToExcel(payload);
            setExportLoading(false);
          }}
          dataTestId={formatDataTestId('Export to Excel', 'button')}
        />
      </Box>
      <Box class="ag-theme-alpine flex-1!">
        <AgGridSolid
          ref={gridRef}
          overlayNoRowsTemplate="No data available"
          overlayLoadingTemplate={atgLogo}
          columnDefs={props.orderHistoryColumns}
          gridOptions={{
            defaultColDef: { flex: 1, filter: true },
            onGridReady: onGridReady,
            rowHeight: 50,
            rowModelType: 'serverSide',
            pagination: true,
            paginationPageSize: 50,
            cacheBlockSize: 50,
            paginationPageSizeSelector: false,
          }}
          loadingOverlayComponent={atgLogo}
          onRowClicked={(e) => {
            if (e.node.rowPinned) return;
            const orderId = (e.data as { orderId: number }).orderId;
            if (routeOrderDetails(orderId)) {
              window.open(`/order/details/${orderId}`, '_blank');
            }
          }}
          suppressPaginationPanel={false}
          pagination={true}
          noRowsOverlayComponent={atgLogo}
        />
      </Box>
    </Stack>
  );
};

export default OrderHistoryGrid;
